import { useCallback, useEffect, useState } from 'react';
import {
  anularInvitacion,
  crearInvitacionRpc,
  listarEquipo,
  listarInvitaciones,
  type InvitacionDelNegocio,
  type PersonaDelEquipo,
} from '../../lib/api-negocio';
import { Boton, Campo, CampoLista, Esqueleto, Insignia, MensajeError, Tarjeta, TextoAyuda } from '../../ui/base';

const NOMBRE_ROL: Record<string, string> = { propietario: 'Dueño', cajero: 'Cajero', contador: 'Contador' };

function estadoDe(inv: InvitacionDelNegocio): 'pendiente' | 'aceptada' | 'vencida' {
  if (inv.aceptadaEn) return 'aceptada';
  return new Date(inv.expiraEn).getTime() > Date.now() ? 'pendiente' : 'vencida';
}

/**
 * Quiénes están en el negocio, con nombre y rol. El dueño además invita cajeros o al contador con un
 * enlace de un solo correo; el cajero y el contador solo ven la lista.
 */
export function SeccionEquipo({ negocioId, soloLectura = false }: { negocioId: string; soloLectura?: boolean }) {
  const [equipo, setEquipo] = useState<PersonaDelEquipo[] | null>(null);
  const [errorEquipo, setErrorEquipo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [invCorreo, setInvCorreo] = useState('');
  const [invRol, setInvRol] = useState('cajero');
  const [invEnlace, setInvLink] = useState('');
  const [invError, setInvError] = useState('');
  const [invitaciones, setInvitaciones] = useState<InvitacionDelNegocio[]>([]);

  const cargarEquipo = useCallback(() => {
    setErrorEquipo('');
    listarEquipo(negocioId)
      .then(setEquipo)
      .catch((e) => {
        setEquipo(null);
        setErrorEquipo(e instanceof Error ? e.message : 'No pudimos leer el equipo.');
      });
  }, [negocioId]);
  useEffect(cargarEquipo, [cargarEquipo]);

  useEffect(() => {
    if (soloLectura) return;
    void listarInvitaciones(negocioId).then(setInvitaciones).catch(() => setInvitaciones([]));
  }, [negocioId, soloLectura]);

  /** Genera el enlace de una invitación y refresca la lista; la nueva reemplaza a la pendiente. */
  function generarEnlace(correo: string, rol: string) {
    setOcupado(true);
    setInvError('');
    crearInvitacionRpc(negocioId, correo, rol)
      .then((token) => {
        setInvLink(`${location.origin}/invitacion?token=${token}`);
        return listarInvitaciones(negocioId).then(setInvitaciones);
      })
      .catch((e) => setInvError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setOcupado(false));
  }

  return (
    <Tarjeta>
      <h2 className="mb-2 font-extrabold">Equipo</h2>
      {!soloLectura && <h3 className="mb-1 font-bold">Quiénes están</h3>}
      {errorEquipo ? (
        <div className="flex flex-wrap items-center gap-3">
          <MensajeError e2e="equipo-error">{errorEquipo}</MensajeError>
          <Boton variante="secundario" onClick={cargarEquipo}>
            Reintentar
          </Boton>
        </div>
      ) : equipo === null ? (
        <Esqueleto lineas={3} />
      ) : (
        <ul className="divide-border mb-4 divide-y" data-e2e="equipo-lista">
          {equipo.map((p, i) => (
            <li key={`${p.nombre}-${i}`} className="flex items-center justify-between gap-3 py-2" data-e2e="equipo-fila">
              <span className="font-semibold">{p.nombre}</span>
              <span className="text-muted text-contenido">
                {NOMBRE_ROL[p.rol] ?? p.rol}
                {p.soyYo && ' (tú)'}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!soloLectura && (
        <>
          <h3 className="mb-1 font-bold">Invitar</h3>
          <TextoAyuda>
            Invita cajeros o a tu contador: genera el enlace y compárteselo. Solo sirve para ese correo, vence en 7 días,
            y quien lo abre entra con el código que le llega a su correo. Un enlace nuevo para el mismo correo reemplaza
            al anterior.
          </TextoAyuda>
          <div className="grid gap-3 sm:grid-cols-3">
            <Campo etiqueta="Correo" type="email" value={invCorreo} onChange={(e) => setInvCorreo(e.target.value)} />
            <CampoLista etiqueta="Rol" value={invRol} onChange={(e) => setInvRol(e.target.value)}>
              <option value="cajero">Cajero</option>
              <option value="contador">Contador</option>
              <option value="propietario">Dueño</option>
            </CampoLista>
            <div className="flex items-end">
              <Boton
                className="w-full"
                disabled={ocupado || !invCorreo.includes('@')}
                onClick={() => generarEnlace(invCorreo, invRol)}
              >
                Generar enlace
              </Boton>
            </div>
          </div>
          {invEnlace && (
            <div className="bg-info/10 rounded-control mt-3 flex items-center justify-between gap-2 px-3 py-2">
              <span className="num min-w-0 flex-1 truncate text-etiqueta" data-e2e="invitacion-enlace">{invEnlace}</span>
              <Boton variante="secundario" onClick={() => void navigator.clipboard.writeText(invEnlace)}>Copiar</Boton>
            </div>
          )}
          <MensajeError>{invError}</MensajeError>
          {invitaciones.length > 0 && (
            <ul className="divide-border mt-3 divide-y" data-e2e="invitaciones-lista">
              {invitaciones.map((inv) => {
                const estado = estadoDe(inv);
                return (
                  <li key={inv.id} className="flex flex-wrap items-center gap-2 py-2" data-e2e="invitacion-fila">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{inv.correo}</p>
                      <p className="text-muted text-etiqueta">
                        {NOMBRE_ROL[inv.rol] ?? inv.rol}
                        {estado === 'pendiente' && ` · vence el ${new Date(inv.expiraEn).toLocaleDateString('es-CO')}`}
                      </p>
                    </div>
                    {estado === 'pendiente' && <Insignia tono="aviso">PENDIENTE</Insignia>}
                    {estado === 'aceptada' && <Insignia tono="exito">ACEPTADA</Insignia>}
                    {estado === 'vencida' && <Insignia tono="neutro">VENCIDA</Insignia>}
                    {estado !== 'aceptada' && (
                      <>
                        <Boton
                          variante="secundario"
                          disabled={ocupado}
                          onClick={() => generarEnlace(inv.correo, inv.rol)}
                        >
                          Nuevo enlace
                        </Boton>
                        <Boton
                          variante="secundario"
                          disabled={ocupado}
                          onClick={() => {
                            setOcupado(true);
                            setInvError('');
                            anularInvitacion(inv.id)
                              .then(() => listarInvitaciones(negocioId).then(setInvitaciones))
                              .catch((e) => setInvError(e instanceof Error ? e.message : 'Error'))
                              .finally(() => setOcupado(false));
                          }}
                        >
                          Anular
                        </Boton>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </Tarjeta>
  );
}
